import { FC, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Check } from '@mui/icons-material';
import RemoveIcon from '@mui/icons-material/Remove';
import AddIcon from '@mui/icons-material/Add';

import styles from './TripTimeline.module.scss';
import { TripDetail, TripStop } from '../../../../../types/trip.types';
import { formatDateWithWeekday } from '../../../../../utils/dateUtils';
import { TodayStopIcon } from '../../../../../components/Icons/TodayStopIcon';
import { UpcomingDayIcon } from '../../../../../components/Icons/UpcomingDayIcon';
import TimelineStop from '../TimelineStop/TimelineStop';
import { getDayStatus } from '../../utils/activityUtils';
import { useAppSelector } from '../../../../../hooks/redux';
import { useCurrencySymbol } from '../../../../../hooks/useCurrencySymbol';

interface TripTimelineProps {
  trip: TripDetail;
  onShowStopDetails: (activity: TripStop) => void;
  selectedActivityId: number | null;
  onCloseDetails: () => void;
}

const TripTimeline: FC<TripTimelineProps> = ({ trip, onShowStopDetails, selectedActivityId, onCloseDetails }) => {
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());
  const user = useAppSelector(state => state.user);
  const currencySymbol = useCurrencySymbol();

  const getActivityDate = (activities: any[]): string => {
    if (!activities || activities.length === 0) return trip.start_date;

    const activity = activities[0];

    if (activity.start_date_time) return activity.start_date_time;
    if (activity.departure_date_time) return activity.departure_date_time;
    if (activity.check_in_date_time) return activity.check_in_date_time;

    return trip.start_date;
  };

  const toggleDayCollapse = (dayKey: string) => {
    const newCollapsedDays = new Set(collapsedDays);
    if (collapsedDays.has(dayKey)) {
      newCollapsedDays.delete(dayKey);
    } else {
      newCollapsedDays.add(dayKey);
    }
    setCollapsedDays(newCollapsedDays);
  };

  const isDayCollapsed = (dayKey: string) => collapsedDays.has(dayKey);

  const sortedDays = Object.keys(trip.trip_stops_by_day).sort((a, b) => {
    const activitiesA = trip.trip_stops_by_day[a];
    const activitiesB = trip.trip_stops_by_day[b];

    const dateA = new Date(getActivityDate(activitiesA));
    const dateB = new Date(getActivityDate(activitiesB));

    return dateA.getTime() - dateB.getTime();
  });

  return (
    <Box className={styles.timeline}>
      <Box className={`${styles.timelineContainer} ${sortedDays.length === 0 ? styles.emptyTimeline : ''}`}>
        {sortedDays.map((dayKey, dayIndex) => {
          const activities = trip.trip_stops_by_day[dayKey];
          const dayBudget = trip.total_budget_by_day[dayKey] || 0;
          const dayStatus = getDayStatus(activities);
          const isCollapsed = isDayCollapsed(dayKey);

          return (
            <Box
              key={dayKey}
              className={styles.timelineDay}
            >
              <Box
                className={`${styles.dayIcon} ${
                  styles[`dayIcon${dayStatus.charAt(0).toUpperCase() + dayStatus.slice(1)}`]
                }`}
              >
                {dayStatus === 'completed' && <Check sx={{ fontSize: 20, color: '#ffffff' }} />}
                {dayStatus === 'active' && <TodayStopIcon size={40} />}
                {dayStatus === 'upcoming' && <UpcomingDayIcon size={40} />}
              </Box>
              <Box
                className={styles.dayHeader}
                onClick={() => toggleDayCollapse(dayKey)}
              >
                <Box className={styles.dayHeaderContent}>
                  <Typography
                    variant="h7Bold"
                    className={styles.dayTitle}
                  >
                    {formatDateWithWeekday(getActivityDate(activities))}
                  </Typography>
                </Box>
                <Box className={styles.dayExpandIcon}>{isCollapsed ? <AddIcon /> : <RemoveIcon />}</Box>
              </Box>
              {!isCollapsed && (
                <>
                  <Box className={`${styles.dayActivities} ${isCollapsed ? styles.collapsed : styles.expanded}`}>
                    {activities.map((activity, activityIndex) => (
                      <TimelineStop
                        key={activity.id}
                        activity={activity}
                        dayStatus={dayStatus}
                        isLast={dayIndex === sortedDays.length - 1 && activityIndex === activities.length - 1}
                        onShowDetails={onShowStopDetails}
                        isSelected={selectedActivityId === activity.id}
                        onCloseDetails={onCloseDetails}
                      />
                    ))}
                  </Box>
                  {dayBudget > 0 && user.show_trip_budgets && (
                    <Box className={styles.dayTotal}>
                      <Typography
                        variant="h10Bold"
                        className={styles.totalText}
                      >
                        Total Budget
                      </Typography>
                      <Typography
                        variant="h10Bold"
                        className={styles.totalAmount}
                      >
                        {currencySymbol}
                        {dayBudget}
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {user.show_trip_budgets && (
        <Box className={styles.tripTotal}>
          <Typography
            variant="h8Bold"
            className={styles.totalBudget}
          >
            Total Budget: {currencySymbol}
            {trip.total_budget}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default TripTimeline;
